import React, { useCallback } from "react"
import { Button } from "@silversea-ssc/ui-components-react"
import { Trans } from "@lingui/macro"
import { Occupancy } from "@ssc-booking/api-contracts"
import { useECommerceMachine } from "../../app-core/_common/machines/machines-context"
import styles from "./ChangeGuests.module.scss"
import { guestsMessageContent } from "./guests-message-content"

const maxGuests = 4
const minAdults = 1
const minKids = 0

const increment = (key: keyof Occupancy, prev: Occupancy): Occupancy => ({ ...prev, [key]: prev[key] + 1 })
const decrement = (key: keyof Occupancy, prev: Occupancy): Occupancy => ({ ...prev, [key]: prev[key] - 1 })

const canIncrementAdults = (occupancy: Occupancy): boolean =>
    Occupancy.totalGuests(occupancy) < maxGuests && !(occupancy.adults >= 2 && occupancy.kids > 0)
const canDecrementAdults = (occupancy: Occupancy): boolean => occupancy.adults > minAdults
const canIncrementKids = (occupancy: Occupancy): boolean =>
    Occupancy.totalGuests(occupancy) < maxGuests && occupancy.adults <= 2
const canDecrementKids = (occupancy: Occupancy): boolean => occupancy.kids > minKids

const incrementAdults = (prev: Occupancy): Occupancy => (canIncrementAdults(prev) ? increment("adults", prev) : prev)
const decrementAdults = (prev: Occupancy): Occupancy => (canDecrementAdults(prev) ? decrement("adults", prev) : prev)
const incrementKids = (prev: Occupancy): Occupancy => (canIncrementKids(prev) ? increment("kids", prev) : prev)
const decrementKids = (prev: Occupancy): Occupancy => (canDecrementKids(prev) ? decrement("kids", prev) : prev)

type OnChange = (o: Occupancy) => void

type Props = {
    value: Occupancy
    onChange: OnChange
}
const ChangeGuests: React.FC<React.PropsWithChildren<Props>> = ({ value, onChange }) => {
    const [state] = useECommerceMachine()
    const occupancy = value

    const onIncreaseAdults = useCallback(() => onChange(incrementAdults(value)), [value, onChange])
    const onDecreaseAdults = useCallback(() => onChange(decrementAdults(value)), [value, onChange])
    const onIncreaseKids = useCallback(() => onChange(incrementKids(value)), [value, onChange])
    const onDecreaseKids = useCallback(() => onChange(decrementKids(value)), [value, onChange])

    const guestsMessage = guestsMessageContent(state.context.guests)

    return (
        <div className={styles.root}>
            <div className={styles.content}>
                <div className={styles.guestsRow}>
                    <div className={styles.label}>
                        <Trans>Adults</Trans>
                    </div>
                    <div className={styles.controller}>
                        <Button
                            renderAs="button"
                            onClick={onDecreaseAdults}
                            iconName="minus"
                            disabled={!canDecrementAdults(occupancy)}
                            dataContentPiece="decrease-adults"
                        />
                        <div className={styles.value}>{occupancy.adults}</div>
                        <Button
                            renderAs="button"
                            onClick={onIncreaseAdults}
                            iconName="plus"
                            disabled={!canIncrementAdults(occupancy)}
                            dataContentPiece="increase-adults"
                        />
                    </div>
                </div>

                <div className={styles.guestsRow}>
                    <div className={styles.label}>
                        <Trans>Children</Trans>
                        <div className={styles.subLabel}>
                            <Trans>Under 17 years</Trans>
                        </div>
                    </div>
                    <div className={styles.controller}>
                        <Button
                            renderAs="button"
                            onClick={onDecreaseKids}
                            iconName="minus"
                            disabled={!canDecrementKids(occupancy)}
                            dataContentPiece="decrease-children"
                        />
                        <div className={styles.value}>{occupancy.kids}</div>
                        <Button
                            renderAs="button"
                            onClick={onIncreaseKids}
                            iconName="plus"
                            disabled={!canIncrementKids(occupancy)}
                            dataContentPiece="increase-children"
                        />
                    </div>
                </div>
            </div>
            {guestsMessage && (
                <div className={styles.guestsMessageContainer}>
                    <div className={styles.guestsMessageTitle}>{guestsMessage.title}</div>
                    <div>{guestsMessage.content}</div>
                </div>
            )}
        </div>
    )
}

export default React.memo<React.FC<Props>>(ChangeGuests)
